import { supabase } from './supabase';
import { saveFmxCredentialsRequest, clearFmxCredentialsRequest } from './apiClient';

async function dbQuery(queryFn, fallback) {
  try {
    const { data, error } = await queryFn();
    if (error) return fallback;
    return data ?? fallback;
  } catch {
    return fallback;
  }
}

async function dbMutate(mutationFn) {
  try {
    const { error } = await mutationFn();
    return !error;
  } catch {
    return false;
  }
}

// --- PROJECTS ---

export async function getProjects() {
  return dbQuery(
    () => supabase.from('projects').select('*').order('updated_at', { ascending: false }),
    []
  );
}

// Credentials are NOT written here. Callers create the project row first, then
// save credentials through saveProjectCredentials so they get encrypted server-side.
export async function createProject(name, description, fmxSiteUrl, userId = null) {
  return dbQuery(
    () => supabase.from('projects').insert({
      name,
      description: description || null,
      fmx_site_url: fmxSiteUrl || null,
      user_id: userId || null,
    }).select().single(),
    null
  );
}

export async function getProjectsByOwner(userId) {
  return dbQuery(
    () => supabase.from('projects').select('*').eq('user_id', userId).order('updated_at', { ascending: false }),
    []
  );
}

export async function getOtherProjects(userId) {
  return dbQuery(
    () => supabase.from('projects').select('*').or(`user_id.neq.${userId},user_id.is.null`).order('updated_at', { ascending: false }),
    []
  );
}

export async function updateProjectOwner(projectId, newOwnerId) {
  return dbQuery(
    () => supabase.from('projects').update({ user_id: newOwnerId }).eq('id', projectId).select().single(),
    null
  );
}

export async function getProjectByFmxUrl(fmxSiteUrl) {
  return dbQuery(
    () => supabase.from('projects').select('id, name, user_id, fmx_site_url').eq('fmx_site_url', fmxSiteUrl).limit(1).maybeSingle(),
    null
  );
}

// --- PROFILES ---

// Profile SELECTs try the feature_permissions column first but gracefully
// fall back to the legacy column set if migration 018 has not been applied
// yet — otherwise a missing column fails the query at PostgREST and
// `currentProfile` ends up null across the app.
export async function getAllProfiles() {
  const withFlags = await dbQuery(
    () => supabase.from('profiles').select('id, full_name, email, role, feature_permissions'),
    null
  );
  if (withFlags !== null) return withFlags;
  return dbQuery(
    () => supabase.from('profiles').select('id, full_name, email, role'),
    []
  );
}

export async function getCurrentProfile(userId) {
  const withFlags = await dbQuery(
    () => supabase.from('profiles').select('id, full_name, email, role, feature_permissions').eq('id', userId).single(),
    null
  );
  if (withFlags) return withFlags;
  return dbQuery(
    () => supabase.from('profiles').select('id, full_name, email, role').eq('id', userId).single(),
    null
  );
}

// Feature-flag helpers. Admins implicitly have every feature.
// Shape on profiles.feature_permissions: { "<key>": true }.
export function hasFeature(profile, key) {
  if (!profile) return false;
  if (profile.role === 'admin') return true;
  const flags = profile.feature_permissions || {};
  return flags[key] === true;
}

export async function setUserFeature(userId, key, enabled) {
  // Read-modify-write the JSONB column. Admin RLS policy guards this call.
  const { data: current } = await supabase
    .from('profiles')
    .select('feature_permissions')
    .eq('id', userId)
    .single();
  const next = { ...(current?.feature_permissions || {}) };
  if (enabled) next[key] = true;
  else delete next[key];
  return dbQuery(
    () => supabase.from('profiles').update({ feature_permissions: next }).eq('id', userId).select('id, feature_permissions').single(),
    null
  );
}

export async function getImportSummaryForProjects(projectIds) {
  if (!projectIds || projectIds.length === 0) return [];
  return dbQuery(
    // project_imports has no updated_at column; completed_at (set on insert,
    // and rows are otherwise immutable apart from push-outcome columns) is
    // the record's timestamp.
    () => supabase.from('project_imports').select('project_id, schema_type, row_count_stored, row_count, completed_at, pushed_count, failed_count, push_status').in('project_id', projectIds),
    []
  );
}

export async function updateProfileRole(userId, newRole) {
  return dbQuery(
    () => supabase.from('profiles').update({ role: newRole }).eq('id', userId).select().single(),
    null
  );
}

export async function updateProfileName(userId, fullName) {
  return dbQuery(
    () => supabase.from('profiles').update({ full_name: fullName }).eq('id', userId).select().single(),
    null
  );
}

// Claim an unassigned project. The .is('user_id', null) guard prevents racing claims.
export async function claimProject(projectId, userId) {
  return dbQuery(
    () => supabase.from('projects').update({ user_id: userId }).eq('id', projectId).is('user_id', null).select().single(),
    null
  );
}

// Invoke the delete-user Edge Function. Returns { success: true } or { error: '...' }.
export async function deleteUserViaEdgeFunction(userId) {
  try {
    const { data, error } = await supabase.functions.invoke('delete-user', { body: { userId } });
    if (error) return { error: error.message || 'Failed to delete user' };
    return data || { error: 'Unknown response' };
  } catch (e) {
    return { error: e?.message || 'Network error' };
  }
}

// Invoke the create-user Edge Function. Returns { success: true, userId } or { error: '...' }.
export async function createUserViaEdgeFunction({ email, fullName, role, password }) {
  try {
    const { data, error } = await supabase.functions.invoke('create-user', {
      body: { email, fullName, role, password },
    });
    if (error) {
      // Edge function non-2xx responses include the JSON body on error.context
      try {
        const body = await error.context?.json?.();
        if (body?.error) return { error: body.error };
      } catch { /* fall through */ }
      return { error: error.message || 'Failed to create user' };
    }
    return data || { error: 'Unknown response' };
  } catch (e) {
    return { error: e?.message || 'Network error' };
  }
}

// Routed through /api/fmx-credentials so the password is encrypted server-side
// (AES-256-GCM) before hitting the database. The browser never writes the
// plaintext credential directly to Supabase, and the stored value is not
// recoverable without FMX_CRED_KEY.
export async function saveProjectCredentials({ projectId, siteUrl, email, password, verified }) {
  try {
    return await saveFmxCredentialsRequest({ projectId, siteUrl, email, password, verified });
  } catch (e) {
    console.error('saveProjectCredentials failed:', e);
    return null;
  }
}

export async function clearProjectCredentials(projectId) {
  try {
    return await clearFmxCredentialsRequest(projectId);
  } catch (e) {
    console.error('clearProjectCredentials failed:', e);
    return null;
  }
}

export async function updateProjectModules(projectId, modules) {
  return dbQuery(
    () => supabase.from('projects').update({ fmx_modules: modules }).eq('id', projectId).select().single(),
    null
  );
}

// Cache uses a single sentinel row per (project_id, schema_type):
//   record_type = 'custom_fields_cache', fmx_name = '__cache__'
//   extra = { customFields: [...] }

export async function getFmxReferenceCache(projectId, schemaType) {
  return dbQuery(
    () => supabase.from('fmx_reference_cache')
      .select('fmx_name, fmx_id, extra, record_type, cached_at')
      .eq('project_id', projectId)
      .eq('schema_type', schemaType)
      .eq('record_type', 'custom_fields_cache')
      .eq('fmx_name', '__cache__')
      .maybeSingle(), // zero rows = first sync for this schema, not an error (single() 406s)
    null
  );
}

export async function saveFmxReferenceCache(projectId, schemaType, customFields, systemFields = [], enrichmentVersion = null) {
  return dbMutate(
    () => supabase.from('fmx_reference_cache').upsert({
      project_id: projectId,
      schema_type: schemaType,
      record_type: 'custom_fields_cache',
      fmx_name: '__cache__',
      fmx_id: null,
      extra: { customFields, systemFields, enrichmentVersion },
      cached_at: new Date().toISOString(),
    }, { onConflict: 'project_id,schema_type,record_type,fmx_name' })
  );
}

// --- DEPENDENCY CACHE ---

export async function saveDependencyCache(projectId, depKey, items, totalCount) {
  try {
    const { error } = await supabase
      .from('fmx_reference_cache')
      .upsert({
        project_id: projectId,
        schema_type: depKey,
        record_type: 'dependency_cache',
        fmx_name: '__dep_cache__',
        fmx_id: null,
        extra: { items, totalCount },
        cached_at: new Date().toISOString(),
      }, { onConflict: 'project_id,schema_type,record_type,fmx_name' });
    return !error;
  } catch {
    return false;
  }
}

export async function getAllDependencyCaches(projectId) {
  try {
    const { data, error } = await supabase
      .from('fmx_reference_cache')
      .select('schema_type, extra, cached_at')
      .eq('project_id', projectId)
      .eq('record_type', 'dependency_cache');
    if (error) return [];
    return data || [];
  } catch {
    return [];
  }
}

// Delete per-module dependency buckets (`{baseKey}:{slug}`) whose slug is no
// longer present in a fresh sync, so renamed/disabled modules don't leave
// orphan rows behind. keepKeys are full schema_type values (`baseKey:slug`).
export async function deleteStaleDependencyCaches(projectId, baseKey, keepKeys) {
  try {
    const { data, error } = await supabase
      .from('fmx_reference_cache')
      .select('schema_type')
      .eq('project_id', projectId)
      .eq('record_type', 'dependency_cache')
      .like('schema_type', `${baseKey}:%`);
    if (error || !data) return false;
    const stale = data.map(r => r.schema_type).filter(k => !keepKeys.includes(k));
    if (stale.length === 0) return true;
    const { error: delError } = await supabase
      .from('fmx_reference_cache')
      .delete()
      .eq('project_id', projectId)
      .eq('record_type', 'dependency_cache')
      .in('schema_type', stale);
    return !delError;
  } catch {
    return false;
  }
}

export async function getDependencyCache(projectId, depKey) {
  try {
    const { data, error } = await supabase
      .from('fmx_reference_cache')
      .select('schema_type, extra, cached_at')
      .eq('project_id', projectId)
      .eq('schema_type', depKey)
      .eq('record_type', 'dependency_cache')
      .maybeSingle();
    if (error) return null;
    return data || null;
  } catch {
    return null;
  }
}

export async function getCacheAge(projectId, schemaType) {
  try {
    const cached = await getFmxReferenceCache(projectId, schemaType);
    if (!cached) return Infinity;
    const ageMs = Date.now() - new Date(cached.cached_at).getTime();
    return ageMs / 3600000;
  } catch {
    return Infinity;
  }
}

export async function updateProject(projectId, updates) {
  return dbQuery(
    () => supabase.from('projects').update(updates).eq('id', projectId).select().single(),
    null
  );
}

export async function updateCardSetting(projectId, schemaType, settingKey, value) {
  try {
    const { data: project } = await supabase
      .from('projects').select('card_settings').eq('id', projectId).single();
    const current = project?.card_settings || {};
    const entry = current[schemaType] || {};
    const updated = { ...current, [schemaType]: { ...entry, [settingKey]: value } };
    return updateProject(projectId, { card_settings: updated });
  } catch (e) {
    console.error('updateCardSetting exception:', e);
    return null;
  }
}

// Clear a project's OCR scan state so the scan can be re-run from zero: the
// cached per-attachment extractions (equipment_ocr_results) and the applied-
// write log (equipment_scan_writes). Leaves the project + FMX connection
// intact. Returns true on success.
export async function resetProjectScanData(projectId) {
  return dbMutate(async () => {
    const cache = await supabase.from('equipment_ocr_results').delete().eq('project_id', projectId);
    if (cache.error) return cache;
    return await supabase.from('equipment_scan_writes').delete().eq('project_id', projectId);
  });
}

export async function deleteProject(projectId) {
  // Best-effort Storage cleanup — Postgres cascades remove the DB rows but not
  // Storage objects. Gather the PDF keys BEFORE the rows are deleted. A Storage
  // failure must never block the project delete.
  try {
    const { data: runs } = await supabase
      .from('extraction_runs').select('storage_key').eq('project_id', projectId);
    const pdfKeys = (runs || []).map(r => r.storage_key).filter(Boolean);
    if (pdfKeys.length) await supabase.storage.from('pdf-uploads').remove(pdfKeys);
    // Field-builder session sources live in the same bucket but are keyed on
    // field_builder_sessions (their extraction_runs rows share the key only
    // when extraction succeeded, so gather both).
    const { data: fbSessions } = await supabase
      .from('field_builder_sessions').select('storage_key').eq('project_id', projectId);
    const fbKeys = (fbSessions || []).map(r => r.storage_key).filter(Boolean);
    if (fbKeys.length) await supabase.storage.from('pdf-uploads').remove(fbKeys);
    const { data: decks } = await supabase.storage.from('qbr-decks').list(projectId);
    const deckKeys = (decks || []).map(o => `${projectId}/${o.name}`);
    if (deckKeys.length) await supabase.storage.from('qbr-decks').remove(deckKeys);
  } catch {
    // ignore — Storage cleanup is best-effort
  }

  // Defensive: project_imports and project_reference_values predate the repo
  // migrations and their FK cascade to projects can't be confirmed, so remove
  // them explicitly first (safe whether or not a cascade exists).
  try {
    await supabase.from('project_imports').delete().eq('project_id', projectId);
    await supabase.from('project_reference_values').delete().eq('project_id', projectId);
  } catch {
    // ignore — the row delete below is the authoritative step
  }

  // Delete the project row; the remaining child tables cascade at the DB level,
  // and the encrypted fmx_credentials column drops with the row.
  return dbMutate(
    () => supabase.from('projects').delete().eq('id', projectId)
  );
}

// --- MAPPING MEMORY ---

export async function getMappingSuggestions(schemaType, headers) {
  try {
    const data = await dbQuery(
      () => supabase.from('mapping_memory')
        .select('source_header, fmx_field, confidence')
        .eq('schema_type', schemaType)
        .in('source_header', headers),
      []
    );

    const result = {};
    for (const row of data) {
      result[row.source_header] = {
        fmxField: row.fmx_field,
        confidence: row.confidence,
      };
    }
    return result;
  } catch {
    return {};
  }
}

export async function saveMappings(schemaType, mappings) {
  const rows = Object.entries(mappings).map(([fmxField, sourceHeader]) => ({
    schema_type: schemaType,
    source_header: sourceHeader,
    fmx_field: fmxField,
    confidence: 1,
    last_used_at: new Date().toISOString(),
  }));
  if (rows.length === 0) return true;
  return dbMutate(
    () => supabase.from('mapping_memory').upsert(rows, {
      onConflict: 'schema_type,source_header',
      ignoreDuplicates: false,
    })
  );
}

// --- RULE MEMORY ---

export async function getSavedRule(schemaType, fmxField) {
  return dbQuery(
    () => supabase.from('rule_memory').select('*')
      .eq('schema_type', schemaType)
      .eq('fmx_field', fmxField)
      .limit(1)
      .maybeSingle(), // no saved rule yet is the common case
    null
  );
}

export async function saveRule(schemaType, fmxField, instruction, code) {
  return dbMutate(
    () => supabase.from('rule_memory').upsert({
      schema_type: schemaType,
      fmx_field: fmxField,
      instruction,
      generated_code: code,
      use_count: 1,
      last_used_at: new Date().toISOString(),
    }, { onConflict: 'schema_type,fmx_field' })
  );
}

// --- DATA PATTERNS ---

export async function getDataPatterns(schemaType, fmxField) {
  const data = await dbQuery(
    () => supabase.from('data_pattern_memory')
      .select('sample_values, pattern_hint')
      .eq('schema_type', schemaType)
      .eq('fmx_field', fmxField)
      .limit(1)
      .maybeSingle(), // no stored pattern yet is the common case
    null
  );
  if (!data) return null;
  return { sampleValues: data.sample_values, patternHint: data.pattern_hint };
}

export async function saveDataPatterns(schemaType, fieldPatterns) {
  const rows = fieldPatterns.map(({ fmxField, sampleValues, patternHint }) => ({
    schema_type: schemaType,
    fmx_field: fmxField,
    sample_values: sampleValues,
    pattern_hint: patternHint,
    confidence: 1,
    last_used_at: new Date().toISOString(),
  }));
  if (rows.length === 0) return true;
  return dbMutate(
    () => supabase.from('data_pattern_memory').upsert(rows, { onConflict: 'schema_type,fmx_field' })
  );
}

export async function getSavedRulesForSchema(schemaType) {
  try {
    const data = await dbQuery(
      () => supabase.from('rule_memory')
        .select('fmx_field, instruction, generated_code')
        .eq('schema_type', schemaType),
      []
    );
    const result = {};
    for (const row of data) {
      result[row.fmx_field] = { instruction: row.instruction, code: row.generated_code };
    }
    return result;
  } catch {
    return {};
  }
}

// --- REFERENCE VALUES ---

export async function getReferenceValues(projectId, schemaType) {
  try {
    const data = await dbQuery(
      () => supabase.from('project_reference_values')
        .select('field_name, value')
        .eq('project_id', projectId)
        .eq('schema_type', schemaType),
      []
    );
    const result = {};
    for (const row of data) {
      if (!result[row.field_name]) result[row.field_name] = [];
      result[row.field_name].push(row.value);
    }
    return result;
  } catch {
    return {};
  }
}

export async function completeImport(
  projectId,
  schemaType,
  rowCount,
  mappingSnapshot,
  referenceValues,
  rowsData = [],
  importName = null,
  sourceFilename = null
) {
  try {
    // Auto-generate import name if not provided
    let name = importName;
    if (!name) {
      const { count } = await supabase
        .from('project_imports')
        .select('*', { count: 'exact', head: true })
        .eq('project_id', projectId)
        .eq('schema_type', schemaType);
      const n = (count || 0) + 1;
      name = `${schemaType} - ${String(n).padStart(2, '0')}`;
    }

    const ROW_CAP = 5000;
    const capped = Array.isArray(rowsData) ? rowsData.slice(0, ROW_CAP) : [];
    const truncated = Array.isArray(rowsData) && rowsData.length > ROW_CAP;

    const { data: importRecord, error: importError } = await supabase
      .from('project_imports')
      .insert({
        project_id: projectId,
        schema_type: schemaType,
        row_count: rowCount,
        mapping_snapshot: mappingSnapshot,
        completed_at: new Date().toISOString(),
        import_name: name,
        rows_data: capped,
        row_count_stored: capped.length,
        source_filename: sourceFilename || null,
        truncated,
      })
      .select('id')
      .single();
    if (importError) return null;

    if (referenceValues && referenceValues.length > 0) {
      const rows = referenceValues.map(({ fieldName, value }) => ({
        project_id: projectId,
        schema_type: schemaType,
        field_name: fieldName,
        value,
      }));

      const { error: refError } = await supabase
        .from('project_reference_values')
        .upsert(rows, {
          onConflict: 'project_id,schema_type,field_name,value',
          ignoreDuplicates: true,
        });
      if (refError) return null;
    }

    return importRecord?.id ?? true;
  } catch {
    return null;
  }
}

export async function getProjectImports(projectId) {
  return dbQuery(
    () => supabase.from('project_imports')
      .select('id, schema_type, import_name, row_count, row_count_stored, completed_at, source_filename, truncated, mapping_snapshot, pushed_count, failed_count, push_status')
      .eq('project_id', projectId)
      .order('completed_at', { ascending: false }),
    []
  );
}

// Record the FMX push outcome on an import record. NULL push_status means the
// import was mapped/downloaded but never pushed.
export async function updateImportPushOutcome(importId, { pushedCount, failedCount }) {
  if (!importId) return false;
  const status = failedCount > 0 ? (pushedCount > 0 ? 'partial' : 'failed') : 'pushed';
  return dbMutate(
    () => supabase.from('project_imports')
      .update({ pushed_count: pushedCount, failed_count: failedCount, push_status: status })
      .eq('id', importId)
  );
}

export async function getImportRows(importId) {
  const data = await dbQuery(
    () => supabase.from('project_imports').select('rows_data').eq('id', importId).single(),
    null
  );
  return data?.rows_data ?? [];
}

export async function renameImport(importId, newName) {
  return dbMutate(
    () => supabase.from('project_imports').update({ import_name: newName }).eq('id', importId)
  );
}

// --- FMX PUSHES (for Undo) ---

export async function savePush(projectId, {
  schemaType, mode, siteUrl, endpointBase,
  createdIds, snapshots, rowCount, succeeded, failed,
}) {
  try {
    const { data, error } = await supabase
      .from('project_pushes')
      .insert({
        project_id: projectId,
        schema_type: schemaType,
        mode,
        fmx_site_url: siteUrl,
        endpoint_base: endpointBase,
        created_ids: createdIds ?? null,
        update_snapshots: snapshots ?? null,
        row_count: rowCount,
        succeeded,
        failed,
      })
      .select('id')
      .single();
    if (error) return null;
    return data?.id ?? null;
  } catch {
    return null;
  }
}

// Cross-project push feed for the Dashboard/Activity screens.
export async function getRecentPushes({ sinceDays = 30, limit = 200 } = {}) {
  const since = new Date(Date.now() - sinceDays * 86400000).toISOString();
  return dbQuery(
    () => supabase.from('project_pushes')
      .select('id, project_id, schema_type, row_count, succeeded, failed, fmx_site_url, pushed_at')
      .gte('pushed_at', since)
      .order('pushed_at', { ascending: false })
      .limit(limit),
    []
  );
}

export async function getProjectPushes(projectId) {
  return dbQuery(
    () => supabase.from('project_pushes')
      .select('id, schema_type, mode, fmx_site_url, endpoint_base, row_count, succeeded, failed, pushed_at, undone_at, undo_result')
      .eq('project_id', projectId)
      .order('pushed_at', { ascending: false }),
    []
  );
}

export async function getPush(pushId) {
  return dbQuery(
    () => supabase.from('project_pushes').select('*').eq('id', pushId).single(),
    null
  );
}

export async function markPushUndone(pushId, undoResult) {
  return dbMutate(
    () => supabase.from('project_pushes')
      .update({ undone_at: new Date().toISOString(), undo_result: undoResult })
      .eq('id', pushId)
  );
}

// --- PROMPTS (PDF extraction) ---

// `fieldKey` addresses per-field variants of the Equipment OCR stage.
// Pass null (the default) for the stage-level overall prompt; pass a slug
// from equipmentOcrFields.js to target that field's dedicated prompt.
export async function getActivePrompt(migrationType, stage = 'extraction', fieldKey = null) {
  return dbQuery(
    () => {
      let q = supabase.from('prompts')
        .select('id, migration_type, stage, field_key, version, body, active, notes')
        .eq('migration_type', migrationType)
        .eq('stage', stage)
        .eq('active', true);
      q = fieldKey == null ? q.is('field_key', null) : q.eq('field_key', fieldKey);
      return q.limit(1).maybeSingle();
    },
    null
  );
}

export async function getAllPrompts() {
  return dbQuery(
    () => supabase.from('prompts')
      .select('id, migration_type, stage, field_key, version, body, active, notes, created_at, created_by')
      .order('migration_type')
      .order('stage')
      .order('version', { ascending: false }),
    []
  );
}

// Adds a new version for (migration_type, stage, field_key); if makeActive, flips off any prior
// active row in the same triple so the single-active uniqueness constraint is preserved.
export async function createPromptVersion({ migrationType, stage = 'extraction', fieldKey = null, body, notes, makeActive = true, createdBy = null }) {
  try {
    let sel = supabase.from('prompts')
      .select('version')
      .eq('migration_type', migrationType)
      .eq('stage', stage);
    sel = fieldKey == null ? sel.is('field_key', null) : sel.eq('field_key', fieldKey);
    const { data: existing } = await sel.order('version', { ascending: false }).limit(1);
    const nextVersion = (existing?.[0]?.version || 0) + 1;

    if (makeActive) {
      let upd = supabase.from('prompts')
        .update({ active: false })
        .eq('migration_type', migrationType)
        .eq('stage', stage)
        .eq('active', true);
      upd = fieldKey == null ? upd.is('field_key', null) : upd.eq('field_key', fieldKey);
      await upd;
    }

    const { data, error } = await supabase.from('prompts')
      .insert({
        migration_type: migrationType,
        stage,
        field_key: fieldKey,
        version: nextVersion,
        body,
        notes: notes || null,
        active: makeActive,
        created_by: createdBy,
      })
      .select()
      .single();
    if (error) return null;
    return data;
  } catch {
    return null;
  }
}

export async function activatePromptVersion(promptId) {
  try {
    const { data: target } = await supabase.from('prompts')
      .select('migration_type, stage, field_key')
      .eq('id', promptId)
      .single();
    if (!target) return false;
    let upd = supabase.from('prompts')
      .update({ active: false })
      .eq('migration_type', target.migration_type)
      .eq('stage', target.stage)
      .eq('active', true);
    upd = target.field_key == null ? upd.is('field_key', null) : upd.eq('field_key', target.field_key);
    await upd;
    const { error } = await supabase.from('prompts')
      .update({ active: true })
      .eq('id', promptId);
    return !error;
  } catch {
    return false;
  }
}

// --- EXTRACTION RUNS ---

export async function createExtractionRun({ projectId, userId, migrationType, stage = 'extraction', storageKey, sourceFilename, pageCount, promptId, promptVersion, dryRun = false, dryRunSourceRunId = null }) {
  return dbQuery(
    () => supabase.from('extraction_runs').insert({
      project_id: projectId || null,
      user_id: userId || null,
      migration_type: migrationType,
      stage,
      storage_key: storageKey || null,
      source_filename: sourceFilename || null,
      page_count: pageCount || null,
      prompt_id: promptId || null,
      prompt_version: promptVersion || null,
      status: 'running',
      dry_run: !!dryRun,
      dry_run_source_run_id: dryRunSourceRunId || null,
    }).select().single(),
    null
  );
}

export async function completeExtractionRun(runId, { status, resultJson, error, durationMs, inputTokens, outputTokens, estimatedCostUsd }) {
  return dbMutate(
    () => supabase.from('extraction_runs').update({
      status,
      result_json: resultJson || null,
      error: error || null,
      duration_ms: durationMs || null,
      input_tokens: inputTokens ?? null,
      output_tokens: outputTokens ?? null,
      estimated_cost_usd: estimatedCostUsd ?? null,
    }).eq('id', runId)
  );
}

export async function getExtractionRuns(projectId) {
  return dbQuery(
    () => supabase.from('extraction_runs')
      .select('id, migration_type, source_filename, page_count, prompt_version, status, error, duration_ms, created_at')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false })
      .limit(50),
    []
  );
}

// Return the set of equipment IDs that already have a completed OCR run on
// this project. The writer side lives in src/equipmentOcr.js where
// `source_filename` is set to `equipment#<id>` when the extraction_run is
// created — keep the two in sync if the format ever changes.
export async function listOcrdEquipmentIds(projectId) {
  if (!projectId) return new Set();
  try {
    const { data, error } = await supabase.from('extraction_runs')
      .select('source_filename')
      .eq('project_id', projectId)
      .eq('migration_type', 'Equipment')
      .eq('stage', 'ocr')
      .eq('status', 'complete')
      .like('source_filename', 'equipment#%');
    if (error || !data) return new Set();
    const ids = new Set();
    for (const row of data) {
      const sf = row.source_filename || '';
      const id = sf.startsWith('equipment#') ? sf.slice('equipment#'.length) : '';
      if (id) ids.add(String(id));
    }
    return ids;
  } catch {
    return new Set();
  }
}

// --- EQUIPMENT OCR CACHE (equipment_ocr_results) ---
//
// Per-(project, equipment, attachment) cache so repeat scans cost zero
// tokens. The fields jsonb is shallow-merged on upsert via the
// merge_equipment_ocr_fields() Postgres function (migration 023).

export async function getEquipmentOcrResults(projectId, equipmentId) {
  if (!projectId || equipmentId == null) return [];
  return dbQuery(
    () => supabase.from('equipment_ocr_results')
      .select('id, project_id, equipment_id, attachment_id, attachment_filename, attachment_content_type, fields, last_run_id, created_at, updated_at')
      .eq('project_id', projectId)
      .eq('equipment_id', String(equipmentId))
      .order('updated_at', { ascending: false }),
    []
  );
}

// Batch-mode prefetch. Returns Map<equipmentId, rows[]>.
export async function getEquipmentOcrResultsByEquipmentIds(projectId, equipmentIds) {
  const out = new Map();
  if (!projectId || !Array.isArray(equipmentIds) || equipmentIds.length === 0) return out;
  const ids = Array.from(new Set(equipmentIds.map(String)));
  const rows = await dbQuery(
    () => supabase.from('equipment_ocr_results')
      .select('id, equipment_id, attachment_id, attachment_filename, attachment_content_type, fields, last_run_id, updated_at')
      .eq('project_id', projectId)
      .in('equipment_id', ids),
    []
  );
  for (const r of rows || []) {
    const key = String(r.equipment_id);
    if (!out.has(key)) out.set(key, []);
    out.get(key).push(r);
  }
  return out;
}

// Upsert with shallow JSONB merge of fieldsDelta into the existing row.
// Incoming wins on key collision. Returns the merged row or null on failure.
export async function upsertEquipmentOcrResult({
  projectId,
  equipmentId,
  attachmentId,
  attachmentFilename = null,
  attachmentContentType = null,
  fieldsDelta = {},
  runId = null,
}) {
  if (!projectId || equipmentId == null || attachmentId == null) return null;
  return dbQuery(
    () => supabase.rpc('merge_equipment_ocr_fields', {
      p_project_id: projectId,
      p_equipment_id: String(equipmentId),
      p_attachment_id: String(attachmentId),
      p_attachment_filename: attachmentFilename,
      p_attachment_content_type: attachmentContentType,
      p_fields_delta: fieldsDelta || {},
      p_last_run_id: runId || null,
    }),
    null
  );
}

// Per-project summary of OCR'd equipment for the History view: one entry
// per equipment_id, with attachment count, the union of distinct field
// labels we've extracted, and the most-recent updated_at across the
// equipment's attachments. Grouping is done client-side because Supabase
// REST doesn't expose generic aggregations and the per-project row count
// is small (one row per attachment).
export async function getProjectOcrHistory(projectId) {
  if (!projectId) return [];
  const rows = await dbQuery(
    () => supabase.from('equipment_ocr_results')
      .select('equipment_id, attachment_id, attachment_filename, attachment_content_type, fields, updated_at, last_run_id')
      .eq('project_id', projectId)
      .order('updated_at', { ascending: false }),
    []
  );
  const byEquipment = new Map();
  for (const r of rows || []) {
    const key = String(r.equipment_id);
    let agg = byEquipment.get(key);
    if (!agg) {
      agg = {
        equipmentId: key,
        attachmentCount: 0,
        fieldLabels: new Set(),
        lastUpdated: r.updated_at || null,
        lastRunId: r.last_run_id || null,
      };
      byEquipment.set(key, agg);
    }
    agg.attachmentCount += 1;
    for (const label of Object.keys(r.fields || {})) agg.fieldLabels.add(label);
    const t = r.updated_at ? new Date(r.updated_at).getTime() : 0;
    const cur = agg.lastUpdated ? new Date(agg.lastUpdated).getTime() : 0;
    if (t > cur) {
      agg.lastUpdated = r.updated_at;
      agg.lastRunId = r.last_run_id || agg.lastRunId;
    }
  }
  return Array.from(byEquipment.values())
    .map(a => ({ ...a, fieldLabels: Array.from(a.fieldLabels), fieldCount: a.fieldLabels.size }))
    .sort((a, b) => {
      const ta = a.lastUpdated ? new Date(a.lastUpdated).getTime() : 0;
      const tb = b.lastUpdated ? new Date(b.lastUpdated).getTime() : 0;
      return tb - ta;
    });
}

// Equipment IDs (per project) that have ANY cache row. Sibling to
// listOcrdEquipmentIds — that one reads extraction_runs (audit log); this
// one reads the actual cache table and is what batch-mode should consult
// for "already scanned" pre-filtering.
export async function listCachedEquipmentIds(projectId) {
  if (!projectId) return new Set();
  try {
    const { data, error } = await supabase.from('equipment_ocr_results')
      .select('equipment_id')
      .eq('project_id', projectId);
    if (error || !data) return new Set();
    const ids = new Set();
    for (const row of data) {
      if (row.equipment_id) ids.add(String(row.equipment_id));
    }
    return ids;
  } catch {
    return new Set();
  }
}

// --- ATTACHMENT FIELD SCAN WRITE LOG (equipment_scan_writes) ---
//
// One row per approved apply action; `writes` holds the field-level detail
// including previous values so the Applied history view can revert a
// record (migration 029).

export async function listScanWrites(projectId) {
  if (!projectId) return [];
  return dbQuery(
    () => supabase.from('equipment_scan_writes')
      .select('id, project_id, equipment_id, equipment_tag, building_name, equipment_type_name, batch_num, applied_by, applied_by_name, writes, reverted, reverted_at, created_at')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false }),
    []
  );
}

export async function createScanWrite({
  projectId,
  equipmentId,
  equipmentTag = null,
  buildingName = null,
  equipmentTypeName = null,
  batchNum = null,
  appliedBy = null,
  appliedByName = null,
  writes = [],
}) {
  if (!projectId || equipmentId == null) return null;
  return dbQuery(
    () => supabase.from('equipment_scan_writes')
      .insert({
        project_id: projectId,
        equipment_id: String(equipmentId),
        equipment_tag: equipmentTag,
        building_name: buildingName,
        equipment_type_name: equipmentTypeName,
        batch_num: batchNum,
        applied_by: appliedBy,
        applied_by_name: appliedByName,
        writes,
      })
      .select().single(),
    null
  );
}

export async function setScanWriteReverted(writeId) {
  if (!writeId) return null;
  return dbQuery(
    () => supabase.from('equipment_scan_writes')
      .update({ reverted: true, reverted_at: new Date().toISOString() })
      .eq('id', writeId)
      .select().single(),
    null
  );
}

// Total estimated Claude spend on OCR scans for this project (header stat).
export async function getOcrSpendUsd(projectId) {
  if (!projectId) return 0;
  try {
    const { data, error } = await supabase.from('extraction_runs')
      .select('estimated_cost_usd')
      .eq('project_id', projectId)
      .eq('stage', 'ocr');
    if (error || !data) return 0;
    return data.reduce((sum, r) => sum + (Number(r.estimated_cost_usd) || 0), 0);
  } catch {
    return 0;
  }
}

export async function getExtractionRun(runId) {
  return dbQuery(
    () => supabase.from('extraction_runs')
      .select('id, project_id, user_id, migration_type, stage, storage_key, source_filename, page_count, prompt_id, prompt_version, status, result_json, error, duration_ms, input_tokens, output_tokens, estimated_cost_usd, dry_run, dry_run_source_run_id, created_at')
      .eq('id', runId)
      .single(),
    null
  );
}

export async function getPromptById(promptId) {
  return dbQuery(
    () => supabase.from('prompts')
      .select('id, migration_type, stage, version, body, active, notes, created_at')
      .eq('id', promptId)
      .single(),
    null
  );
}

export async function getCorrectionsForRun(runId) {
  return dbQuery(
    () => supabase.from('corrections')
      .select('id, correction_type, field_path, row_index, original_value, corrected_value, reviewed, promoted_example_id, created_at')
      .eq('extraction_run_id', runId)
      .order('created_at', { ascending: true }),
    []
  );
}

// Returns a short-lived signed URL for downloading the original PDF from
// Storage, or null if the run has no storage_key / the fetch fails.
export async function getPdfSignedUrl(storageKey, expiresInSeconds = 300) {
  if (!storageKey) return null;
  try {
    const { data, error } = await supabase.storage
      .from('pdf-uploads')
      .createSignedUrl(storageKey, expiresInSeconds);
    if (error) return null;
    return data?.signedUrl || null;
  } catch {
    return null;
  }
}

// Downloads a PDF from Storage as a File object — used by the re-run flow.
export async function downloadPdfFromStorage(storageKey, filename = 'run.pdf') {
  if (!storageKey) return null;
  try {
    const { data, error } = await supabase.storage
      .from('pdf-uploads')
      .download(storageKey);
    if (error || !data) return null;
    return new File([data], filename, { type: 'application/pdf' });
  } catch {
    return null;
  }
}

export async function getAllExtractionRuns({ limit = 100 } = {}) {
  return dbQuery(
    () => supabase.from('extraction_runs')
      .select('id, project_id, user_id, migration_type, stage, source_filename, page_count, prompt_id, prompt_version, status, error, duration_ms, input_tokens, output_tokens, estimated_cost_usd, dry_run, dry_run_source_run_id, created_at')
      .order('created_at', { ascending: false })
      .limit(limit),
    []
  );
}

// Lists recent *successful*, *non-dry-run* runs of a given (migration_type, stage)
// that can be used as a dry-run sample. Ordered most-recent-first. Extraction
// runs must have a storage_key so we can actually re-render the PDF.
export async function listRecentRunsForReplay(migrationType, stage = 'extraction', limit = 25) {
  try {
    let q = supabase.from('extraction_runs')
      .select('id, migration_type, stage, storage_key, source_filename, page_count, result_json, input_tokens, output_tokens, estimated_cost_usd, duration_ms, created_at')
      .eq('migration_type', migrationType)
      .eq('stage', stage)
      .eq('status', 'complete')
      .eq('dry_run', false)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (stage === 'extraction') q = q.not('storage_key', 'is', null);
    const { data, error } = await q;
    if (error) return [];
    return data || [];
  } catch {
    return [];
  }
}

// --- CORRECTIONS (PDF extraction learning loop) ---

// Writes a batch of corrections in a single round-trip. Caller passes an array
// of { correctionType, fieldPath, rowIndex, originalValue, correctedValue }.
export async function recordCorrections({ extractionRunId, migrationType, userId, entries }) {
  if (!entries?.length) return true;
  const rows = entries.map(e => ({
    extraction_run_id: extractionRunId || null,
    migration_type: migrationType,
    correction_type: e.correctionType,
    field_path: e.fieldPath,
    row_index: e.rowIndex ?? null,
    original_value: e.originalValue ?? null,
    corrected_value: e.correctedValue ?? null,
    user_id: userId || null,
  }));
  return dbMutate(() => supabase.from('corrections').insert(rows));
}

export async function getCorrections({ migrationType, reviewed, limit = 500 } = {}) {
  try {
    let q = supabase.from('corrections')
      .select('id, extraction_run_id, migration_type, correction_type, field_path, row_index, original_value, corrected_value, reviewed, promoted_example_id, created_at')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (migrationType) q = q.eq('migration_type', migrationType);
    if (reviewed !== undefined) q = q.eq('reviewed', reviewed);
    const { data, error } = await q;
    if (error) return [];
    return data || [];
  } catch {
    return [];
  }
}

export async function markCorrectionsReviewed(ids) {
  if (!ids?.length) return true;
  return dbMutate(
    () => supabase.from('corrections').update({ reviewed: true }).in('id', ids)
  );
}

// --- PROMPT EXAMPLES (few-shot) ---

export async function getExamplesForPrompt(promptId) {
  return dbQuery(
    () => supabase.from('prompt_examples')
      .select('id, prompt_id, example_json, label, enabled, use_count, last_used_at, promoted_from_correction_id, created_at')
      .eq('prompt_id', promptId)
      .order('created_at', { ascending: false }),
    []
  );
}

export async function getEnabledExamplesForPrompt(promptId) {
  return dbQuery(
    () => supabase.from('prompt_examples')
      .select('id, example_json, label')
      .eq('prompt_id', promptId)
      .eq('enabled', true)
      .order('created_at', { ascending: true }),
    []
  );
}

// Promote a correction → prompt_example. Links both directions:
//   corrections.promoted_example_id ↔ prompt_examples.promoted_from_correction_id
export async function promoteCorrectionToExample({ correctionId, promptId, exampleJson, label, createdBy }) {
  try {
    const { data: example, error } = await supabase.from('prompt_examples')
      .insert({
        prompt_id: promptId,
        example_json: exampleJson,
        label: label || null,
        enabled: true,
        promoted_from_correction_id: correctionId || null,
        created_by: createdBy || null,
      })
      .select()
      .single();
    if (error) return null;
    if (correctionId) {
      await supabase.from('corrections')
        .update({ promoted_example_id: example.id, reviewed: true })
        .eq('id', correctionId);
    }
    return example;
  } catch {
    return null;
  }
}

export async function setExampleEnabled(exampleId, enabled) {
  return dbMutate(
    () => supabase.from('prompt_examples').update({ enabled }).eq('id', exampleId)
  );
}

export async function deleteExample(exampleId) {
  return dbMutate(
    () => supabase.from('prompt_examples').delete().eq('id', exampleId)
  );
}

// Fire-and-forget: bumps use counters after an extraction injects examples.
// One set-based statement via the increment_example_usage RPC (migration 030)
// instead of a SELECT + per-id UPDATE fan-out; small race windows are fine.
export async function incrementExampleUsage(exampleIds) {
  if (!exampleIds?.length) return true;
  try {
    const { error } = await supabase.rpc('increment_example_usage', { p_ids: exampleIds });
    return !error;
  } catch {
    return false;
  }
}

// ─── Field overrides (admin-editable required-flag rules) ──────────────
// Migration 017. The override table lets admins correct a field's required
// status without a redeploy. Lookup precedence at validation time is:
//   admin override  >  API /post-options  >  FMX_FIELD_ENRICHMENTS default.

/**
 * Fetch all field overrides. Returned as a nested map
 *   { [schemaType]: { [fieldName]: { is_required, notes, updated_at } } }
 * so callers can do `overrides[schemaType]?.[fieldName]?.is_required`.
 * Includes both base schemas ("Work Task") and module-qualified variants.
 */
export async function getFieldOverrides() {
  try {
    const { data, error } = await supabase
      .from('field_overrides')
      .select('schema_type, field_name, is_required, notes, updated_at');
    if (error) return {};
    const map = {};
    for (const row of data || []) {
      if (!map[row.schema_type]) map[row.schema_type] = {};
      map[row.schema_type][row.field_name] = {
        is_required: row.is_required,
        notes: row.notes,
        updated_at: row.updated_at,
      };
    }
    return map;
  } catch {
    return {};
  }
}

/**
 * Upsert a single override row. Pass `isRequired=null` to explicitly record
 * "no opinion" (falls back to API/enrichment). Pass the whole field in
 * `values` if you want to clear both is_required and notes.
 * Admin-only by RLS.
 */
export async function upsertFieldOverride(schemaType, fieldName, values, userId) {
  try {
    const payload = {
      schema_type: schemaType,
      field_name: fieldName,
      is_required: values.is_required ?? null,
      notes: values.notes ?? null,
      updated_by: userId || null,
    };
    const { error } = await supabase
      .from('field_overrides')
      .upsert(payload, { onConflict: 'schema_type,field_name' });
    return !error;
  } catch {
    return false;
  }
}

/**
 * Delete an override row (admin-only). Useful when an admin wants to
 * revert to API/enrichment defaults without leaving a null-valued row.
 */
export async function deleteFieldOverride(schemaType, fieldName) {
  try {
    const { error } = await supabase
      .from('field_overrides')
      .delete()
      .eq('schema_type', schemaType)
      .eq('field_name', fieldName);
    return !error;
  } catch {
    return false;
  }
}

// --- CUSTOM FIELD BUILDER (field_builder_* tables, migration 033) ---
//
// Sessions and extraction snapshots follow the normal swallow-errors
// convention. The creation-ledger insert (Milestone 3) is the documented
// exception — it must THROW on failure so a field is never POSTed to FMX
// without a confirmed ledger row.

export async function listFieldBuilderSessions(projectId) {
  if (!projectId) return [];
  return dbQuery(
    () => supabase.from('field_builder_sessions')
      .select('id, project_id, created_by, source_kind, source_filename, storage_key, page_count, status, scope, error, created_at, updated_at')
      .eq('project_id', projectId)
      .order('updated_at', { ascending: false }),
    []
  );
}

export async function getFieldBuilderSession(sessionId) {
  return dbQuery(
    () => supabase.from('field_builder_sessions').select('*').eq('id', sessionId).single(),
    null
  );
}

export async function createFieldBuilderSession({ projectId, createdBy, sourceKind, sourceFilename, storageKey, pageCount }) {
  return dbQuery(
    () => supabase.from('field_builder_sessions').insert({
      project_id: projectId,
      created_by: createdBy || null,
      source_kind: sourceKind,
      source_filename: sourceFilename || null,
      storage_key: storageKey || null,
      page_count: pageCount ?? null,
      status: 'extracting',
    }).select().single(),
    null
  );
}

// Patch keys use the table's snake_case column names (status, scope, draft,
// error, page_count). Autosave calls this debounced with { draft }.
export async function updateFieldBuilderSession(sessionId, patch) {
  return dbQuery(
    () => supabase.from('field_builder_sessions')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', sessionId)
      .select()
      .single(),
    null
  );
}

export async function deleteFieldBuilderSession(sessionId) {
  return dbMutate(
    () => supabase.from('field_builder_sessions').delete().eq('id', sessionId)
  );
}

// Immutable snapshots — insert-only, never updated (per-field revert and the
// consent-based re-extraction merge depend on that).
export async function createFieldBuilderExtraction({ sessionId, projectId, version, stage1Json, stage2Json, stage1RunId, stage2RunId }) {
  return dbQuery(
    () => supabase.from('field_builder_extractions').insert({
      session_id: sessionId,
      project_id: projectId,
      version: version || 1,
      stage1_json: stage1Json ?? null,
      stage2_json: stage2Json ?? null,
      stage1_run_id: stage1RunId || null,
      stage2_run_id: stage2RunId || null,
    }).select().single(),
    null
  );
}

export async function getLatestFieldBuilderExtraction(sessionId) {
  return dbQuery(
    () => supabase.from('field_builder_extractions')
      .select('*')
      .eq('session_id', sessionId)
      .order('version', { ascending: false })
      .limit(1)
      .maybeSingle(),
    null
  );
}

// Stage 2 runs after the human confirms scope, so a snapshot row is written
// in two phases: insert with stage-1 only, then this one-time fill of the
// stage-2 half. The `.is('stage2_json', null)` guard keeps the row immutable
// once complete — a filled snapshot can never be overwritten.
export async function completeFieldBuilderExtractionStage2(extractionId, stage2Json, stage2RunId) {
  return dbQuery(
    () => supabase.from('field_builder_extractions')
      .update({ stage2_json: stage2Json, stage2_run_id: stage2RunId || null })
      .eq('id', extractionId)
      .is('stage2_json', null)
      .select()
      .single(),
    null
  );
}

// --- QBR GENERATOR ---

// Active threshold config per metric. Returns { [metric]: configJson }.
export async function getActiveQbrThresholds() {
  const rows = await dbQuery(
    () => supabase
      .from('qbr_grading_thresholds')
      .select('metric, config, updated_at')
      .eq('is_active', true),
    []
  );
  const byMetric = {};
  for (const r of rows || []) byMetric[r.metric] = r.config;
  return byMetric;
}

// Admin-only. Updates the active threshold row for a metric (history-preserving:
// we keep the prior row by flipping is_active = false rather than overwriting).
export async function updateQbrThresholds(metric, config, userId) {
  try {
    await supabase
      .from('qbr_grading_thresholds')
      .update({ is_active: false })
      .eq('metric', metric)
      .eq('is_active', true);
    const { data, error } = await supabase
      .from('qbr_grading_thresholds')
      .insert({ metric, config, is_active: true, updated_by: userId || null })
      .select()
      .single();
    if (error) return null;
    return data;
  } catch {
    return null;
  }
}

// qbr_runs rows are written server-side by /api/qbr-generate (service
// role) — one insert per generated deck. There is no browser-side
// create/update path.

// ---- QBR templates ----
//
// Admin-managed .pptx files used by the deck generator. The
// upload-and-scan flow runs server-side via /api/qbr-template-upload
// (so we can validate tokens + write to the qbr-templates bucket with
// service-role). Listing + flag toggling go straight to the table via
// RLS — admins have full write access per migration 024.

export async function listQbrTemplates() {
  return dbQuery(
    () => supabase
      .from('qbr_templates')
      .select('id, storage_path, original_filename, label, notes, is_active, is_available, placeholder_map, uploaded_at, uploaded_by')
      .order('uploaded_at', { ascending: false }),
    []
  );
}

// Update a single template's metadata flags. Pass any subset of the
// known editable fields; the caller is responsible for clearing
// is_active on other rows when setting a new default (or wrap the
// call in setDefaultQbrTemplate below).
export async function updateQbrTemplate(id, patch) {
  return dbQuery(
    () => supabase
      .from('qbr_templates').update(patch).eq('id', id).select().single(),
    null
  );
}

// Atomically flip the default flag — clear it on every other row,
// then set it on the target. Two-step write because RLS makes a
// single-statement update awkward across rows; callers should treat
// the brief window between writes as best-effort.
export async function setDefaultQbrTemplate(id) {
  await supabase.from('qbr_templates').update({ is_active: false }).eq('is_active', true);
  return updateQbrTemplate(id, { is_active: true });
}

export async function deleteQbrTemplate(id) {
  return dbMutate(
    () => supabase.from('qbr_templates').delete().eq('id', id)
  );
}

export async function listQbrRuns(projectId) {
  return dbQuery(
    () => supabase
      .from('qbr_runs')
      .select('id, quarter_label, period_start, period_end, status, pptx_storage_path, created_at, user_id')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false }),
    []
  );
}
